import { convexQuery, useConvexAction } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import type { JobPreviewData } from '@cvx/features/jobs/jobs'
import { useSession } from '@livekit/components-react'
import {
  isKrispNoiseFilterSupported,
  KrispNoiseFilter,
} from '@livekit/krisp-noise-filter'
import { Button } from '@rekrytering/ui/components/button'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ConnectionState, Room, TokenSource } from 'livekit-client'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { env } from '@/env.client'
import { AgentSessionProvider } from '@/features/interview/components/agents-ui/agent-session-provider'
import { AgentSessionView_01 } from '@/features/interview/components/agents-ui/blocks/agent-session-view-01/components/agent-session-block'
import { StartAudioButton } from '@/features/interview/components/agents-ui/start-audio-button'

type ScreenerQuestion = JobPreviewData['screenerQuestions'][number]
type AdditionalInfoField = JobPreviewData['additionalInfoFields'][number]

type PreviewStep =
  | { readonly status: 'loading' }
  | { readonly status: 'briefing'; readonly data: JobPreviewData }
  | { readonly status: 'screener'; readonly data: JobPreviewData }
  | { readonly status: 'additional-info'; readonly data: JobPreviewData }
  | { readonly status: 'disqualified' }
  | { readonly status: 'starting-room' }
  | {
      readonly status: 'room'
      readonly livekitToken: string
      readonly roomName: string
    }
  | { readonly status: 'completed' }
  | { readonly status: 'error' }

const isDisqualified = (
  questions: ScreenerQuestion[],
  answers: Record<string, string[]>,
): boolean =>
  questions.some((q) =>
    (answers[q._id] ?? []).some((selected) =>
      q.disqualifyingAnswers.includes(selected),
    ),
  )

const PreviewBriefingStep = ({
  data,
  onReady,
}: {
  data: JobPreviewData
  onReady: () => void
}) => {
  const { t } = useTranslation()
  const [consentChecked, setConsentChecked] = useState(false)

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {data.branding.logoUrl && (
          <img
            src={data.branding.logoUrl}
            alt={data.orgSettings.displayName}
            className="h-10 object-contain"
          />
        )}
        <div>
          <p className="text-sm text-muted-foreground">
            {data.orgSettings.displayName}
          </p>
          <h2 className="text-2xl font-semibold">{data.job.title}</h2>
        </div>
        {data.branding.tagline && (
          <p className="italic text-sm text-muted-foreground">
            {data.branding.tagline}
          </p>
        )}
        {data.branding.website && (
          <a
            href={data.branding.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm underline text-muted-foreground hover:text-foreground"
          >
            {data.branding.website}
          </a>
        )}
      </div>

      <div className="space-y-2 text-sm text-muted-foreground">
        <p>{t('jobs.preview.briefing.aboutToStart')}</p>
        <p>
          {t('jobs.preview.briefing.expectedDuration', {
            minutes: String(data.job.expiryDays),
          })}
        </p>
        <p>{t('jobs.preview.briefing.quietEnvironment')}</p>
      </div>

      <label className="flex cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.target.checked)}
          className="mt-1"
        />
        <span className="text-sm">
          {t('jobs.preview.briefing.consentText')}
          {data.orgSettings.termsUrl && (
            <>
              {' '}
              <a
                href={data.orgSettings.termsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                {t('jobs.preview.briefing.termsOfService')}
              </a>
            </>
          )}
          {data.orgSettings.privacyUrl && (
            <>
              {' '}
              <a
                href={data.orgSettings.privacyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                {t('jobs.preview.briefing.privacyPolicy')}
              </a>
            </>
          )}
        </span>
      </label>

      <button
        onClick={onReady}
        disabled={!consentChecked}
        style={
          data.branding.primaryColor
            ? { backgroundColor: data.branding.primaryColor }
            : undefined
        }
        className="rounded bg-primary px-6 py-3 font-medium text-primary-foreground disabled:opacity-50"
      >
        {t('jobs.preview.briefing.ready')}
      </button>
    </div>
  )
}

const PreviewScreenerStep = ({
  data,
  onNext,
  onDisqualified,
}: {
  data: JobPreviewData
  onNext: () => void
  onDisqualified: () => void
}) => {
  const { t } = useTranslation()
  const [answers, setAnswers] = useState<Record<string, string[]>>({})

  const toggleOption = (questionId: string, option: string, multi: boolean) => {
    setAnswers((prev) => {
      const current = prev[questionId] ?? []
      if (multi) {
        return {
          ...prev,
          [questionId]: current.includes(option)
            ? current.filter((o) => o !== option)
            : [...current, option],
        }
      }
      return { ...prev, [questionId]: [option] }
    })
  }

  const allAnswered = data.screenerQuestions.every(
    (q: ScreenerQuestion) => (answers[q._id] ?? []).length > 0,
  )

  const handleSubmit = () => {
    if (isDisqualified(data.screenerQuestions, answers)) {
      onDisqualified()
    } else {
      onNext()
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">
        {t('jobs.preview.screener.title')}
      </h2>
      {data.screenerQuestions.map((q: ScreenerQuestion) => (
        <div key={q._id} className="space-y-2">
          <p className="font-medium">{q.text}</p>
          <div className="space-y-1">
            {q.options.map((option) => (
              <label
                key={option}
                className="flex cursor-pointer items-center gap-2"
              >
                <input
                  type={q.type === 'multi_choice' ? 'checkbox' : 'radio'}
                  name={q._id}
                  checked={(answers[q._id] ?? []).includes(option)}
                  onChange={() =>
                    toggleOption(q._id, option, q.type === 'multi_choice')
                  }
                />
                {option}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={handleSubmit}
        disabled={!allAnswered}
        className="rounded bg-primary px-4 py-2 text-primary-foreground disabled:opacity-50"
      >
        {t('jobs.preview.screener.continue')}
      </button>
    </div>
  )
}

const PreviewAdditionalInfoStep = ({
  data,
  onNext,
}: {
  data: JobPreviewData
  onNext: () => void
}) => {
  const { t } = useTranslation()
  const [values, setValues] = useState<Record<string, string>>({})

  const allRequiredFilled = data.additionalInfoFields
    .filter((f: AdditionalInfoField) => f.required)
    .every(
      (f: AdditionalInfoField) => (values[f.label] ?? '').trim().length > 0,
    )

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">
        {t('jobs.preview.additionalInfo.title')}
      </h2>
      {data.additionalInfoFields.map((field: AdditionalInfoField) => (
        <div key={field._id} className="space-y-1">
          <label className="block font-medium">
            {field.label}
            {field.required && (
              <span className="ml-1 text-destructive">
                {t('jobs.preview.additionalInfo.required')}
              </span>
            )}
          </label>
          <input
            type={field.type}
            placeholder={field.placeholder}
            value={values[field.label] ?? ''}
            onChange={(e) =>
              setValues((prev) => ({ ...prev, [field.label]: e.target.value }))
            }
            className="w-full rounded border px-3 py-2"
          />
        </div>
      ))}
      <button
        onClick={onNext}
        disabled={!allRequiredFilled}
        className="rounded bg-primary px-4 py-2 text-primary-foreground disabled:opacity-50"
      >
        {t('jobs.preview.additionalInfo.continue')}
      </button>
    </div>
  )
}

const PreviewRoomStep = ({
  livekitToken,
  onCompleted,
}: {
  livekitToken: string
  onCompleted: () => void
}) => {
  const { t } = useTranslation()
  const livekitWsUrl = env.VITE_LIVEKIT_WS_URL

  const tokenSource = useMemo(
    () =>
      TokenSource.literal({
        serverUrl: livekitWsUrl,
        participantToken: livekitToken,
      }),
    [livekitToken, livekitWsUrl],
  )

  const room = useMemo(
    () =>
      new Room({
        audioCaptureDefaults: {
          ...(isKrispNoiseFilterSupported() && {
            processor: KrispNoiseFilter(),
          }),
        },
      }),
    [],
  )

  const [hasConnected, setHasConnected] = useState(false)
  const session = useSession(tokenSource, { room })

  useEffect(() => {
    if (session.isConnected) setHasConnected(true)
  }, [session.isConnected])

  useEffect(() => {
    if (
      session.connectionState === ConnectionState.Disconnected &&
      !session.isConnected &&
      hasConnected
    ) {
      onCompleted()
    }
  }, [session.connectionState, session.isConnected, hasConnected, onCompleted])

  return (
    <AgentSessionProvider session={session}>
      <main className="grid h-full grid-cols-1 place-content-center">
        <AgentSessionView_01
          supportsVideoInput={false}
          supportsScreenShare={false}
          supportsChatInput={true}
          preConnectMessage={t('jobs.preview.room.connecting')}
          className="h-full"
        />
      </main>
      <StartAudioButton label="Start Audio" />
    </AgentSessionProvider>
  )
}

export const PreviewInterviewModal = ({
  jobId,
  onClose,
}: {
  jobId: Id<'jobs'>
  onClose: () => void
}) => {
  const { t } = useTranslation()
  const [step, setStep] = useState<PreviewStep>({ status: 'loading' })

  const { mutateAsync: createMyPreviewRoom } = useMutation({
    mutationFn: useConvexAction(
      api.features.interviews.create_preview_room.createMyPreviewRoom,
    ),
  })

  const { data: previewData } = useQuery(
    convexQuery(api.features.jobs.jobs.getMyJobPreviewData, { jobId }),
  )

  useEffect(() => {
    if (previewData === undefined) return
    if (previewData === null) {
      setStep({ status: 'error' })
      return
    }
    setStep({ status: 'briefing', data: previewData })
  }, [previewData])

  const handleBriefingReady = (data: JobPreviewData) => {
    if (data.screenerQuestions.length > 0) {
      setStep({ status: 'screener', data })
    } else if (data.additionalInfoFields.length > 0) {
      setStep({ status: 'additional-info', data })
    } else {
      startRoom()
    }
  }

  const handleScreenerPassed = (data: JobPreviewData) => {
    if (data.additionalInfoFields.length > 0) {
      setStep({ status: 'additional-info', data })
    } else {
      startRoom()
    }
  }

  const startRoom = async () => {
    setStep({ status: 'starting-room' })
    const result = await createMyPreviewRoom({ jobId })
    if (!result.ok) {
      setStep({ status: 'error' })
      return
    }
    setStep({
      status: 'room',
      livekitToken: result.livekitToken,
      roomName: result.roomName,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="rounded bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-800">
          {t('jobs.preview.banner')}
        </span>
        <Button size="sm" variant="outline" onClick={onClose}>
          {t('jobs.preview.close')}
        </Button>
      </div>

      <div className="flex-1 overflow-auto">
        {(step.status === 'loading' || step.status === 'starting-room') && (
          <div className="flex h-full items-center justify-center">
            <p className="text-muted-foreground">{t('jobs.preview.loading')}</p>
          </div>
        )}

        {step.status === 'error' && (
          <div className="flex h-full items-center justify-center">
            <div className="space-y-3 text-center">
              <p className="text-destructive">{t('jobs.preview.error')}</p>
              <Button variant="outline" onClick={onClose}>
                {t('jobs.preview.close')}
              </Button>
            </div>
          </div>
        )}

        {step.status === 'disqualified' && (
          <div className="flex h-full items-center justify-center">
            <div className="space-y-2 text-center">
              <h1 className="text-xl font-semibold">
                {t('jobs.preview.disqualified.title')}
              </h1>
              <p className="text-muted-foreground">
                {t('jobs.preview.disqualified.body')}
              </p>
            </div>
          </div>
        )}

        {(step.status === 'briefing' ||
          step.status === 'screener' ||
          step.status === 'additional-info') && (
          <div className="mx-auto w-full max-w-lg space-y-8 p-6">
            {step.status === 'briefing' && (
              <PreviewBriefingStep
                data={step.data}
                onReady={() => handleBriefingReady(step.data)}
              />
            )}

            {step.status === 'screener' && (
              <PreviewScreenerStep
                data={step.data}
                onNext={() => handleScreenerPassed(step.data)}
                onDisqualified={() => setStep({ status: 'disqualified' })}
              />
            )}

            {step.status === 'additional-info' && (
              <PreviewAdditionalInfoStep data={step.data} onNext={startRoom} />
            )}
          </div>
        )}

        {step.status === 'room' && (
          <div className="h-full">
            <PreviewRoomStep
              livekitToken={step.livekitToken}
              onCompleted={() => setStep({ status: 'completed' })}
            />
          </div>
        )}

        {step.status === 'completed' && (
          <div className="flex h-full items-center justify-center">
            <div className="space-y-3 text-center">
              <p className="text-muted-foreground">
                {t('jobs.preview.completed')}
              </p>
              <Button variant="outline" onClick={onClose}>
                {t('jobs.preview.close')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
